import React, { useEffect, useState } from "react";
import { Route, Switch, useHistory, useParams } from "react-router-dom";
import ModuleRoutes from "./Routes";
import { useAuth } from "../../auth";
import { Badge } from "../../UIKit/Badge";
import {
  getBadgeType,
  getFlavorLabel,
  getPublicationDateLabel,
  getRemovalDateLabel,
  getStatusLabel,
  tableColumn,
} from "../../UIKit/Table";
import { FormattedMessage, useIntl } from "react-intl";
import { BONotificationPayload } from "../../app/Notifications";
import {
  EMPTY_EXTERNAL_RESOURCE,
  ExternalResource,
  ExternalResourceDTO,
  ExternalResourceLibrary,
} from "./ExternalResource";
import { ExternalResourceLibraryAPI } from "./ExternalResourceLibraryAPI";
import { ExternalResourceForm } from "./ExternalResourceForm";
import { ExternalResourcesList } from "./ExternalResourcesList";
import { Schedulable } from "../News/News";

const emptyDTO: ExternalResourceDTO = {
  headline: "",
  id: "",
  image: undefined,
  imagePath: "",
  triggersNotification: false,
  publicationDate: undefined,
  status: "draft",
  title: "",
  url: "",
};

export function ExternalResourcesModule(props: {
  notify: (not: BONotificationPayload) => void;
}) {
  const history = useHistory();
  const { clientId, supplierId } = useParams<{
    clientId: string;
    supplierId: string;
  }>();
  const auth = useAuth();
  const library = new ExternalResourceLibraryAPI(
    auth.user.token,
    clientId,
    supplierId,
    auth.user.locale
  );
  const intl = useIntl();

  const columns: tableColumn<ExternalResource>[] = [
    {
      label: intl.formatMessage({
        id: "externalResources.title",
        defaultMessage: "Title",
      }),
      content: (resource: ExternalResource) => resource.title,
    },
    {
      label: intl.formatMessage({
        id: "schedulable.status",
        defaultMessage: "Status",
      }),
      content: (item: Schedulable) => (
        <Badge
          label={getStatusLabel(item, intl)}
          flavor={getFlavorLabel(item)}
          type={getBadgeType(item)}
        />
      ),
    },
    {
      label: intl.formatMessage({
        id: "schedulable.publication.date",
        defaultMessage: "Publication date",
      }),
      content: (item: Schedulable, locale) =>
        getPublicationDateLabel(item, locale),
    },
    {
      label: intl.formatMessage({
        id: "schedulable.removal.date",
        defaultMessage: "Removal date",
      }),
      content: (item: Schedulable, locale) => getRemovalDateLabel(item, locale),
    },
    {
      label: intl.formatMessage({
        id: "externalResources.image",
        defaultMessage: "Image",
      }),
      align: "center",
      content: (resource: ExternalResource) =>
        resource.photo ? (
          <img
            alt={resource.title}
            src={resource.photo}
            style={{ maxWidth: "100px", maxHeight: "100px" }}
          />
        ) : (
          ""
        ),
    },
    {
      label: intl.formatMessage({
        id: "externalResources.url",
        defaultMessage: "URL",
      }),
      content: (resource: ExternalResource) => (
        <a href={resource.url} target="_blank" rel="noreferrer">
          {resource.url.slice(0, 30)}...
        </a>
      ),
    },
  ];

  return (
    <Switch>
      <Route path={ModuleRoutes(":clientId", ":supplierId").add} exact>
        <h1 className="text-2xl font-bold py-4 px-2 sm:px-0">
          <FormattedMessage
            id="externalResources.add.title"
            defaultMessage="Add an external resource"
          />
        </h1>
        <ExternalResourceForm
          library={library}
          awaitsValidation={true}
          data={emptyDTO}
          onSuccess={() =>
            history.push(ModuleRoutes(clientId, supplierId).externalResources)
          }
          notify={props.notify}
        />
      </Route>
      <Route path={ModuleRoutes(":clientId", ":supplierId").edit(":id")}>
        <h1 className="text-2xl font-bold py-4 px-2 sm:px-0">
          <FormattedMessage
            id="externalResources.edit.title"
            defaultMessage="Edit an external resource"
          />
        </h1>
        <EditExternalResource
          library={library}
          notify={props.notify}
          clientId={clientId}
          supplierId={supplierId}
        />
      </Route>
      <Route
        path={ModuleRoutes(":clientId", ":supplierId").externalResources}
        exact
      >
        <ExternalResourcesList
          library={library}
          columns={columns}
          clientId={clientId}
          supplierId={supplierId}
        />
      </Route>
    </Switch>
  );
}

function EditExternalResource(props: {
  library: ExternalResourceLibrary;
  notify: (not: BONotificationPayload) => void;
  clientId: string;
  supplierId: string;
}) {
  const [resource, setResource] = useState<ExternalResource>(
    EMPTY_EXTERNAL_RESOURCE
  );
  const { id } = useParams<{ id: string }>();
  useEffect(() => {
    (async () => setResource(await props.library.findOneById(id)))();
  }, [id]);
  const history = useHistory();

  const dto: ExternalResourceDTO = {
    headline: resource.headline,
    id: resource.id,
    image: undefined,
    imagePath: resource.photo,
    triggersNotification: resource.triggersNotification,
    publicationDate: resource.publicationDate,
    removalDate: resource.removalDate,
    status: resource.status,
    title: resource.title,
    url: resource.url,
  };

  return (
    <ExternalResourceForm
      library={props.library}
      awaitsValidation={true}
      data={dto}
      notify={props.notify}
      onSuccess={() =>
        history.push(
          ModuleRoutes(props.clientId, props.supplierId).externalResources
        )
      }
    />
  );
}
